import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isTouchLike } from "../../lib/device";
import { launchTypesApi, typesApi } from "../../api/endpoints";
import type { CardType, LaunchType, LaunchTypeCard, LaunchTypeDay, Stage } from "../../types/domain";
import {
  IcoBoard,
  IcoCheck,
  IcoClock,
  IcoEdit,
  IcoPlus,
  IcoTrash,
} from "../../components/icons";
import { DynamicIcon } from "../../components/DynamicIcon";
import { IconPicker } from "../../components/IconPicker";
import { useCanWrite } from "../../hooks/useCanWrite";
import { useLaunchTypeEvents } from "../../hooks/useLaunchTypeEvents";
import { usePanScroll } from "../../hooks/usePanScroll";
import { AddDayColumn } from "../board/AddDayColumn";
import { LaunchTypeCardModal } from "./LaunchTypeCardModal";
import { DEFAULT_COLORS } from "../../lib/launchTypeMeta";

const STAGE_COLOR: Record<Stage, string> = {
  warmup: "var(--stage-warmup)",
  sales: "var(--stage-sales)",
  close: "var(--stage-close)",
};
const STAGE_LABEL: Record<Stage, string> = {
  warmup: "Прогрев",
  sales: "Продажи",
  close: "Закрытие",
};
const STAGE_MENU: Array<{ id: Stage; label: string; color: string }> = [
  { id: "warmup", label: "Прогрев", color: "var(--stage-warmup)" },
  { id: "sales", label: "Продажи / эфир", color: "var(--stage-sales)" },
  { id: "close", label: "Дожим / закрытие", color: "var(--stage-close)" },
];

type CardModalState =
  | { kind: "closed" }
  | { kind: "create"; dayKey: string }
  | { kind: "edit"; card: LaunchTypeCard };

export function LaunchTypeBoardPage() {
  const { id: ltId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canWrite = useCanWrite();
  const [cardModal, setCardModal] = useState<CardModalState>({ kind: "closed" });
  const [draggingCardId, setDraggingCardId] = useState<string | null>(null);

  // SSE-поток по scope `launch-type:<id>` — подписывает клиент на чужие
  // мутации (переименование дня, правка карточки, добавление/удаление).
  useLaunchTypeEvents(ltId);
  const { onMouseDown: onBoardMouseDown, panning } = usePanScroll();

  const launchTypeQuery = useQuery({
    queryKey: ["launchType", ltId],
    queryFn: async () => (await launchTypesApi.get(ltId!)).launchType,
    enabled: Boolean(ltId),
    // Инвалидация делается явно после каждой mutation; staleTime: Infinity
    // нужен, чтобы refetch по фокусу вкладки не перезаписал optimistic
    // update из moveCardMutation.onMutate до ответа сервера.
    staleTime: Infinity,
  });
  const typesQuery = useQuery({
    queryKey: ["types"],
    queryFn: async () => (await typesApi.list()).types,
  });

  const launchType = launchTypeQuery.data;
  const types = typesQuery.data ?? [];
  const typesById = useMemo(() => {
    const m = new Map<string, CardType>();
    for (const t of types) m.set(t.id, t);
    return m;
  }, [types]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["launchType", ltId] });
    qc.invalidateQueries({ queryKey: ["launchTypes"] });
  };

  const days = useMemo<LaunchTypeDay[]>(() => {
    if (!launchType?.days) return [];
    return [...launchType.days].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [launchType?.days]);

  const cardsByDay = useMemo(() => {
    const map = new Map<string, LaunchTypeCard[]>();
    for (const c of launchType?.cards ?? []) {
      const arr = map.get(c.dayKey) ?? [];
      arr.push(c);
      map.set(c.dayKey, arr);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.position - b.position);
    return map;
  }, [launchType?.cards]);

  const totalCards = (launchType?.cards ?? []).length;

  const addDayMutation = useMutation({
    mutationFn: (vars: { stage: Stage; position?: number }) =>
      launchTypesApi.addDay(ltId!, vars),
    onSuccess: invalidate,
  });
  const patchDayMutation = useMutation({
    mutationFn: (vars: { dayKey: string; patch: { stage?: string; customLabel?: string | null } }) =>
      launchTypesApi.patchDay(ltId!, vars.dayKey, vars.patch),
    onSuccess: invalidate,
  });
  const removeDayMutation = useMutation({
    mutationFn: (dayKey: string) => launchTypesApi.removeDay(ltId!, dayKey),
    onSuccess: invalidate,
  });
  const moveCardMutation = useMutation({
    mutationFn: (vars: { cardId: string; dayKey: string; position: number }) =>
      launchTypesApi.moveCard(ltId!, vars.cardId, {
        dayKey: vars.dayKey,
        position: vars.position,
      }),
    // Optimistic: пересчитываем cards в кэше до ответа сервера, чтобы карточка
    // мгновенно переехала на новое место (как в основном BoardPage).
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ["launchType", ltId] });
      const prev = qc.getQueryData<LaunchType>(["launchType", ltId]);
      if (!prev?.cards) return { prev };
      const moving = prev.cards.find((c) => c.id === vars.cardId);
      if (!moving) return { prev };
      const sourceDay = moving.dayKey;
      // Клонируем каждую карточку перед мутацией position — иначе объекты,
      // на которые всё ещё ссылается `prev`, испортятся и откат в onError
      // восстановит уже мутированное состояние.
      const nextCards = prev.cards
        .filter((c) => c.id !== vars.cardId)
        .map((c) => {
          let position = c.position;
          if (c.dayKey === sourceDay && c.position > moving.position) position -= 1;
          if (c.dayKey === vars.dayKey && position >= vars.position) position += 1;
          return position === c.position ? c : { ...c, position };
        });
      nextCards.push({ ...moving, dayKey: vars.dayKey, position: vars.position });
      qc.setQueryData<LaunchType>(["launchType", ltId], { ...prev, cards: nextCards });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["launchType", ltId], ctx.prev);
    },
    onSettled: invalidate,
  });

  const handleDrop = (dayKey: string, targetIndex: number) => {
    if (!draggingCardId) return;
    const id = draggingCardId;
    setDraggingCardId(null);
    moveCardMutation.mutate({ cardId: id, dayKey, position: targetIndex });
  };

  const dayMutationBusy =
    addDayMutation.isPending || patchDayMutation.isPending || removeDayMutation.isPending;

  if (launchTypeQuery.isLoading) {
    return (
      <div className="launches-empty">
        <p>Загружаем тип запуска…</p>
      </div>
    );
  }
  if (!launchType) {
    return (
      <div className="launches-empty">
        <div className="launches-empty-icon">
          <IcoBoard size={24} stroke="var(--text-muted)" />
        </div>
        <h3>Тип запуска не найден</h3>
        <p>
          Возможно, он удалён. <Link to="/launch-types">Вернуться к списку</Link>.
        </p>
      </div>
    );
  }

  return (
    <>
      <LaunchTypeBoardHead
        launchType={launchType}
        readOnly={!canWrite}
        daysCount={days.length}
        cardsCount={totalCards}
        onPatched={invalidate}
        onBack={() => navigate("/launch-types")}
      />

      <div
        className={`board-scroll${panning ? " is-panning" : ""}`}
        onMouseDown={onBoardMouseDown}
      >
        <div className="board">
          {days.map((d, idx) => (
            <LaunchTypeColumn
              key={d.id}
              day={d}
              dayNum={idx + 1}
              cards={cardsByDay.get(d.id) ?? []}
              typesById={typesById}
              canWrite={canWrite}
              canDelete={days.length > 1}
              dayMutationBusy={dayMutationBusy}
              draggingCardId={draggingCardId}
              onDragStartCard={setDraggingCardId}
              onDragEndCard={() => setDraggingCardId(null)}
              onDrop={handleDrop}
              onOpenCard={(card) => setCardModal({ kind: "edit", card })}
              onAddCard={(dayKey) => setCardModal({ kind: "create", dayKey })}
              onAddDayBefore={() =>
                addDayMutation.mutate({ stage: d.stage, position: idx })
              }
              onAddDayAfter={() =>
                addDayMutation.mutate({ stage: d.stage, position: idx + 1 })
              }
              onChangeStage={(stage) =>
                patchDayMutation.mutate({ dayKey: d.id, patch: { stage } })
              }
              onRenameDay={(customLabel) =>
                patchDayMutation.mutate({ dayKey: d.id, patch: { customLabel } })
              }
              onDeleteDay={() => {
                const count = cardsByDay.get(d.id)?.length ?? 0;
                const msg =
                  count > 0
                    ? `Удалить «День ${idx + 1}» вместе с ${count} ${plural(
                        count,
                        "карточкой",
                        "карточками",
                        "карточками",
                      )}?`
                    : `Удалить «День ${idx + 1}»?`;
                if (window.confirm(msg)) removeDayMutation.mutate(d.id);
              }}
            />
          ))}
          {canWrite && (
            <AddDayColumn
              onAdd={(stage) => addDayMutation.mutate({ stage })}
              disabled={dayMutationBusy}
            />
          )}
        </div>
      </div>

      {cardModal.kind !== "closed" && (
        <LaunchTypeCardModal
          launchTypeId={ltId!}
          state={cardModal}
          days={days}
          types={types}
          onClose={() => setCardModal({ kind: "closed" })}
          onSaved={() => {
            invalidate();
            setCardModal({ kind: "closed" });
          }}
        />
      )}
    </>
  );
}

function pluralEmails(n: number): string {
  if (n === 1) return "письмо";
  if (n > 0 && n < 5) return "письма";
  return "писем";
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

// ────────────────────────────────────────
// Board head — mirrors .board-head layout of BoardPage
// ────────────────────────────────────────

function LaunchTypeBoardHead({
  launchType,
  readOnly,
  daysCount,
  cardsCount,
  onPatched,
  onBack,
}: {
  launchType: LaunchType;
  readOnly: boolean;
  daysCount: number;
  cardsCount: number;
  onPatched: () => void;
  onBack: () => void;
}) {
  const [editing, setEditing] = useState<"name" | "description" | "hint" | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const iconBtnRef = useRef<HTMLButtonElement | null>(null);

  const patch = useMutation({
    mutationFn: (p: Partial<LaunchType>) => launchTypesApi.patch(launchType.id, p as never),
    onSuccess: onPatched,
  });

  return (
    <div className="board-head lt-head">
      <button type="button" className="lt-back" onClick={onBack}>
        ← К списку
      </button>

      <button
        type="button"
        ref={iconBtnRef}
        className="lt-icon-btn"
        onClick={() => !readOnly && setPickerOpen((p) => !p)}
        disabled={readOnly}
        title="Изменить иконку"
        style={{ background: `${launchType.color}20` }}
      >
        <DynamicIcon name={launchType.icon} size={26} color={launchType.color} />
      </button>
      {pickerOpen && (
        <IconPicker
          value={launchType.icon}
          onPick={(name) => patch.mutate({ icon: name })}
          onClose={() => setPickerOpen(false)}
          anchor={iconBtnRef.current}
        />
      )}

      <div className="board-title-wrap" style={{ flex: 1 }}>
        {editing === "name" ? (
          <InlineInput
            value={launchType.name}
            maxLength={60}
            className="lt-title-input"
            onCommit={(v) => {
              const trimmed = v.trim();
              if (trimmed && trimmed !== launchType.name) patch.mutate({ name: trimmed });
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <h1
            className={"lt-title" + (readOnly ? "" : " editable")}
            onClick={() => !readOnly && setEditing("name")}
            title={readOnly ? undefined : "Нажмите, чтобы изменить"}
          >
            {launchType.name}
            {launchType.isBuiltin && (
              <span className="launch-type-badge" style={{ marginLeft: 10 }}>
                Встроенный
              </span>
            )}
          </h1>
        )}
        <div className="board-subtitle">
          Шаблон запуска · <b>{daysCount}</b> {plural(daysCount, "день", "дня", "дней")} ·{" "}
          <b>{cardsCount}</b>{" "}
          {plural(cardsCount, "карточка", "карточки", "карточек")}
          {launchType.description && ` · ${launchType.description}`}
        </div>
        {(launchType.hint || !readOnly) && (
          <>
            {editing === "hint" ? (
              <InlineInput
                value={launchType.hint ?? ""}
                placeholder="Подсказка под карточкой типа…"
                maxLength={200}
                onCommit={(v) => {
                  patch.mutate({ hint: v.trim() || null });
                  setEditing(null);
                }}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <div
                className={"lt-hint-line" + (readOnly ? "" : " editable")}
                onClick={() => !readOnly && setEditing("hint")}
                title={readOnly ? undefined : "Изменить подсказку"}
              >
                {launchType.hint ? (
                  <>💡 {launchType.hint}</>
                ) : (
                  <span className="lt-placeholder">+ добавить подсказку</span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="lt-head-side">
        <div className="lt-color-picker" title="Цвет типа">
          {DEFAULT_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={"lt-color-swatch" + (c === launchType.color ? " active" : "")}
              style={{ background: c }}
              onClick={() => patch.mutate({ color: c })}
              disabled={readOnly}
              title={c}
              aria-label={`Цвет ${c}`}
            />
          ))}
        </div>
        {!readOnly && (
          <button
            type="button"
            className="lt-desc-btn"
            onClick={() => setEditing("description")}
            title="Изменить описание"
          >
            <IcoEdit size={12} /> Описание
          </button>
        )}
      </div>

      {editing === "description" && (
        <div className="lt-desc-editor">
          <InlineInput
            value={launchType.description ?? ""}
            placeholder="Короткое описание типа, отображается на карточке в списке…"
            maxLength={200}
            onCommit={(v) => {
              patch.mutate({ description: v.trim() || null });
              setEditing(null);
            }}
            onCancel={() => setEditing(null)}
          />
        </div>
      )}
    </div>
  );
}

function InlineInput({
  value,
  placeholder,
  onCommit,
  onCancel,
  maxLength,
  className,
}: {
  value: string;
  placeholder?: string;
  onCommit: (v: string) => void;
  onCancel: () => void;
  maxLength?: number;
  className?: string;
}) {
  const [v, setV] = useState(value);
  return (
    <input
      className={"form-input " + (className ?? "")}
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
        if (e.key === "Escape") {
          setV(value);
          onCancel();
        }
      }}
      placeholder={placeholder}
      maxLength={maxLength}
      autoFocus
    />
  );
}

// ────────────────────────────────────────
// Column — reuses the .column + .card CSS of the real board so the editor
// visually matches "настоящую доску", just without dates.
// ────────────────────────────────────────

interface LaunchTypeColumnProps {
  day: LaunchTypeDay;
  dayNum: number;
  cards: LaunchTypeCard[];
  typesById: Map<string, CardType>;
  canWrite: boolean;
  canDelete: boolean;
  dayMutationBusy: boolean;
  draggingCardId: string | null;
  onDragStartCard: (id: string) => void;
  onDragEndCard: () => void;
  onDrop: (dayKey: string, targetIndex: number) => void;
  onOpenCard: (card: LaunchTypeCard) => void;
  onAddCard: (dayKey: string) => void;
  onAddDayBefore: () => void;
  onAddDayAfter: () => void;
  onChangeStage: (stage: Stage) => void;
  onRenameDay: (customLabel: string | null) => void;
  onDeleteDay: () => void;
}

function LaunchTypeColumn({
  day,
  dayNum,
  cards,
  typesById,
  canWrite,
  canDelete,
  dayMutationBusy,
  draggingCardId,
  onDragStartCard,
  onDragEndCard,
  onDrop,
  onOpenCard,
  onAddCard,
  onAddDayBefore,
  onAddDayAfter,
  onChangeStage,
  onRenameDay,
  onDeleteDay,
}: LaunchTypeColumnProps) {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragDepth = useRef(0);
  const [over, setOver] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState("");

  useEffect(() => {
    const reset = () => {
      dragDepth.current = 0;
      setOver(false);
    };
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);
    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const computeTargetIndex = (e: DragEvent<HTMLDivElement>): number => {
    const body = bodyRef.current;
    if (!body) return cards.length;
    const nodes = Array.from(body.querySelectorAll<HTMLElement>(".card[data-card-id]"));
    const y = e.clientY;
    // adjustedIdx — индекс в списке БЕЗ перетаскиваемой карточки; нужен,
    // чтобы drop внутри того же столбца не был off-by-one.
    let adjustedIdx = 0;
    for (const node of nodes) {
      if (draggingCardId && node.dataset.cardId === draggingCardId) continue;
      const rect = node.getBoundingClientRect();
      if (y < rect.top + rect.height / 2) return adjustedIdx;
      adjustedIdx++;
    }
    return adjustedIdx;
  };

  const handleDragEnter = () => {
    dragDepth.current += 1;
    if (!over) setOver(true);
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };
  const handleDragLeave = () => {
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setOver(false);
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepth.current = 0;
    setOver(false);
    const idx = computeTargetIndex(e);
    onDrop(day.id, idx);
  };

  const emailCount = useMemo(
    () =>
      cards.filter((c) => {
        const t = typesById.get(c.typeId);
        return t && /письм|email/i.test(t.name);
      }).length,
    [cards, typesById],
  );

  const defaultLabel = STAGE_LABEL[day.stage];
  const titleText = day.customLabel || defaultLabel;

  const startEditLabel = () => {
    setLabelDraft(day.customLabel || defaultLabel);
    setEditingLabel(true);
    setMenuOpen(false);
    setTimeout(() => inputRef.current?.select(), 10);
  };
  const commitLabel = () => {
    const v = labelDraft.trim();
    onRenameDay(!v || v === defaultLabel ? null : v);
    setEditingLabel(false);
  };
  const cancelLabel = () => setEditingLabel(false);

  return (
    <div className="column">
      <div className="column-head">
        <div className="col-date-row">
          <span className="col-weekday">День {dayNum}</span>
          <div className="col-date-right">
            <span className="col-date" style={{ color: "var(--text-3)" }}>
              {cards.length} шт.
            </span>
            {canWrite && (
              <div className="col-menu-wrap" ref={menuRef}>
                <button
                  type="button"
                  className="col-menu-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen((v) => !v);
                  }}
                  title="Настройки дня"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="5" cy="12" r="1.2" />
                    <circle cx="12" cy="12" r="1.2" />
                    <circle cx="19" cy="12" r="1.2" />
                  </svg>
                </button>
                {menuOpen && (
                  <div className="col-dropdown">
                    <div className="col-dropdown-section">Добавить день</div>
                    <button
                      type="button"
                      className="col-dropdown-item"
                      onClick={() => {
                        if (dayMutationBusy) return;
                        onAddDayBefore();
                        setMenuOpen(false);
                      }}
                      disabled={dayMutationBusy}
                    >
                      <IcoPlus size={13} />
                      Слева от этого дня
                    </button>
                    <button
                      type="button"
                      className="col-dropdown-item"
                      onClick={() => {
                        if (dayMutationBusy) return;
                        onAddDayAfter();
                        setMenuOpen(false);
                      }}
                      disabled={dayMutationBusy}
                    >
                      <IcoPlus size={13} />
                      Справа от этого дня
                    </button>
                    <div className="col-dropdown-section">Этап</div>
                    {STAGE_MENU.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className={
                          "col-dropdown-item" + (day.stage === s.id ? " active" : "")
                        }
                        onClick={() => {
                          if (dayMutationBusy) return;
                          if (day.stage !== s.id) onChangeStage(s.id);
                          setMenuOpen(false);
                        }}
                        disabled={dayMutationBusy}
                      >
                        <span className="stage-dot" style={{ background: s.color }} />
                        {s.label}
                        {day.stage === s.id && (
                          <IcoCheck size={12} stroke="var(--accent)" sw={2.5} />
                        )}
                      </button>
                    ))}
                    <div className="col-dropdown-sep" />
                    <button
                      type="button"
                      className="col-dropdown-item"
                      onClick={startEditLabel}
                    >
                      <IcoEdit size={13} />
                      Переименовать день
                    </button>
                    {day.customLabel && (
                      <button
                        type="button"
                        className="col-dropdown-item"
                        onClick={() => {
                          onRenameDay(null);
                          setMenuOpen(false);
                        }}
                      >
                        Сбросить название
                      </button>
                    )}
                    {canDelete && (
                      <>
                        <div className="col-dropdown-sep" />
                        <button
                          type="button"
                          className="col-dropdown-item col-dropdown-danger"
                          onClick={() => {
                            if (dayMutationBusy) return;
                            onDeleteDay();
                            setMenuOpen(false);
                          }}
                          disabled={dayMutationBusy}
                        >
                          <IcoTrash size={13} />
                          Удалить день
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="col-title">
          <span className="col-day-num">D{dayNum}</span>
          {editingLabel ? (
            <input
              ref={inputRef}
              className="col-title-input"
              value={labelDraft}
              autoFocus
              onChange={(e) => setLabelDraft(e.target.value)}
              onBlur={commitLabel}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitLabel();
                }
                if (e.key === "Escape") {
                  e.preventDefault();
                  cancelLabel();
                }
              }}
              placeholder={defaultLabel}
            />
          ) : (
            <span
              className="col-title-text"
              title={canWrite ? "Кликните, чтобы переименовать" : undefined}
              onClick={canWrite ? startEditLabel : undefined}
            >
              {titleText}
            </span>
          )}
        </div>
        <div className="col-stage">
          <span className="stage-dot" style={{ background: STAGE_COLOR[day.stage] }} />
          {STAGE_LABEL[day.stage]}
        </div>
        <div className="col-meta-row">
          <span className="col-count">
            <b>{emailCount}</b> {pluralEmails(emailCount)}
          </span>
          {canWrite && (
            <button type="button" className="col-add" onClick={() => onAddCard(day.id)}>
              <IcoPlus size={12} /> Добавить
            </button>
          )}
        </div>
      </div>

      <div
        ref={bodyRef}
        className={"col-body" + (over ? " drag-over" : "")}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {cards.map((c) => {
          const type = typesById.get(c.typeId);
          return (
            <LaunchTypeBoardCard
              key={c.id}
              card={c}
              type={type}
              draggable={canWrite}
              isDragging={draggingCardId === c.id}
              onOpen={() => onOpenCard(c)}
              onDragStart={() => onDragStartCard(c.id)}
              onDragEnd={onDragEndCard}
            />
          );
        })}
        {cards.length === 0 && canWrite && (
          <button
            type="button"
            className="card-add-empty"
            onClick={() => onAddCard(day.id)}
          >
            <IcoPlus size={14} /> Добавить карточку
          </button>
        )}
      </div>
    </div>
  );
}

// Визуально совпадает с BoardCard (CSS-класс .card), минус status/presence.
// Удаление и дублирование — внутри модалки (как в основном редакторе).
function LaunchTypeBoardCard({
  card,
  type,
  draggable,
  isDragging,
  onOpen,
  onDragStart,
  onDragEnd,
}: {
  card: LaunchTypeCard;
  type: CardType | undefined;
  draggable: boolean;
  isDragging: boolean;
  onOpen: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const style = type
    ? ({
        "--card-accent": type.color,
        "--type-color": type.color,
        "--type-bg": type.bg,
        "--type-text": type.text,
        cursor: "pointer",
      } as React.CSSProperties)
    : ({ cursor: "pointer" } as React.CSSProperties);

  const previewText = card.preview ? card.preview.replace(/\s+/g, " ").trim() : "";
  // HTML5 DnD ломает touch-скролл на iOS/Android, оставляем только десктоп.
  const canDrag = draggable && !isTouchLike();

  return (
    <div
      className={"card" + (isDragging ? " dragging" : "")}
      style={style}
      onClick={onOpen}
      draggable={canDrag}
      onDragStart={
        canDrag
          ? (e) => {
              e.dataTransfer.effectAllowed = "move";
              try {
                e.dataTransfer.setData("application/x-launchlab-lt-card", card.id);
              } catch {
                // ignore
              }
              onDragStart();
            }
          : undefined
      }
      onDragEnd={canDrag ? onDragEnd : undefined}
      data-card-id={card.id}
    >
      <div className="card-head">
        {type && (
          <span className="type-badge">
            <span className="type-dot" />
            {type.name}
          </span>
        )}
        {card.time && (
          <span className="card-time">
            <IcoClock size={11} />
            {card.time}
          </span>
        )}
      </div>
      <h3 className="card-title">{card.title}</h3>
      {previewText && <p className="card-preview">{previewText}</p>}
    </div>
  );
}
